import { getRoute } from '../api/routeApi';

// 여행지 고정 (화담숲)
export const TRIP_DEST = { label: '화담숲', lat: 37.3419823, lon: 127.2885772 };

// 배정 불가 임계값
const MAX_TIME_DIFF = 40;       // 출발 시간 차이 (분)
const MAX_DEPART_DETOUR = 20;   // 출발 우회 허용 (분, OSRM 기반)
const MAX_RETURN_DETOUR = 20;   // 귀환 우회 허용 (분, OSRM 기반)

const toRad = (deg) => deg * Math.PI / 180;

const haversine = (a, b) => {
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const x = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const timeToMinutes = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

// ── 1인당 2건 OSRM 호출 → 직행 소요시간 사전 조회 ──
export const prefetchRoutes = async (participants, onProgress) => {
    const n = participants.length;
    const tripDest = TRIP_DEST;
    const depRoutes = new Array(n);   // 출발지 → 화담숲
    const retRoutes = new Array(n);   // 화담숲 → 집

    for (let i = 0; i < n; i++) {
        if (onProgress) onProgress(`경로 조회 중 ${i + 1}/${n}`);
        // 2건 순차 호출 (초당 2건 이내)
        depRoutes[i] = await getRoute([participants[i].departure, tripDest]);
        retRoutes[i] = await getRoute([tripDest, participants[i].destination]);
    }

    return { depRoutes, retRoutes };
};

// ── 우회 시간 추정 (OSRM 직행시간 + Haversine 보정) ──
// 드라이버(i)가 라이더(j)를 태우고 목적지(dest)로 갈 때의 우회 시간(분)
const estimateDetourMin = (directRoute, driverLoc, riderLoc, destLoc) => {
    if (!directRoute) return 99999;
    const directKm = directRoute.distance;
    const directMin = directRoute.duration;
    if (directKm === 0) return 99999;

    // 평균 속도 (km/분)
    const avgSpeed = directKm / directMin;
    // Haversine 우회 거리 (km)
    const detour = haversine(driverLoc, riderLoc) + haversine(riderLoc, destLoc) - haversine(driverLoc, destLoc);
    // 우회 시간 추정 (분)
    return avgSpeed > 0 ? detour / avgSpeed : 99999;
};

// Greedy 배정
const greedyAssign = (driverCount, riderCount, seats, scoreMatrix) => {
    const pairs = [];
    for (let di = 0; di < driverCount; di++) {
        for (let ri = 0; ri < riderCount; ri++) {
            pairs.push({ di, ri, score: scoreMatrix[di][ri] });
        }
    }
    pairs.sort((a, b) => a.score - b.score);

    const remaining = [...seats];
    const assigned = new Array(riderCount).fill(-1);

    for (const { di, ri, score } of pairs) {
        if (score >= 99999) continue;
        if (assigned[ri] !== -1) continue;
        if (remaining[di] <= 0) continue;
        assigned[ri] = di;
        remaining[di]--;
    }

    return assigned;
};

const buildGroups = (drivers, riders, assigned) => {
    const groups = drivers.map((d, di) => ({
        driver: d,
        riders: riders.filter((_, ri) => assigned[ri] === di),
        over: riders.filter((_, ri) => assigned[ri] === di).length > d.seats
    }));
    const unassignedAll = riders.filter((_, ri) => assigned[ri] === -1);
    const unassignedCar = unassignedAll.filter(p => p.hasCar);
    unassignedCar.forEach(p => {
        groups.push({ driver: p, riders: [], over: false });
    });
    const unassigned = unassignedAll.filter(p => !p.hasCar);
    return { groups, unassigned };
};

// 차주 조합 생성
const getCombinations = (n) => {
    const results = [];
    for (let mask = 1; mask < (1 << n); mask++) {
        const combo = [];
        for (let i = 0; i < n; i++) {
            if (mask & (1 << i)) combo.push(i);
        }
        results.push(combo);
    }
    return results;
};

// 점수 행렬 생성 (OSRM 직행시간 + Haversine 우회 보정)
const buildScores = (participants, depRoutes, retRoutes) => {
    const n = participants.length;
    const tripDest = TRIP_DEST;

    const depScores = Array.from({ length: n }, () => new Array(n).fill(99999));
    const retScores = Array.from({ length: n }, () => new Array(n).fill(99999));

    for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
            if (i === j) continue;

            const timeDiff = Math.abs(
                timeToMinutes(participants[i].departureTime) -
                timeToMinutes(participants[j].departureTime)
            );

            // 출발: 드라이버(i)가 라이더(j)를 태우고 화담숲으로
            if (timeDiff <= MAX_TIME_DIFF) {
                const extraMin = estimateDetourMin(
                    depRoutes[i],
                    participants[i].departure,
                    participants[j].departure,
                    tripDest
                );
                if (extraMin <= MAX_DEPART_DETOUR) {
                    depScores[i][j] = extraMin * 2.0 + timeDiff * 1.0;
                }
            }

            // 귀환: 드라이버(i)가 라이더(j)를 내려주고 집으로
            const retExtraMin = estimateDetourMin(
                retRoutes[i],
                tripDest,
                participants[j].destination,
                participants[i].destination
            );
            if (retExtraMin <= MAX_RETURN_DETOUR) {
                retScores[i][j] = retExtraMin * 2.0;
            }
        }
    }

    return { depScores, retScores };
};

// ── 차량 배정 ──
export const assignCars = (participants, depRoutes, retRoutes) => {
    if (participants.length < 2) return { departure: null, returnTrip: null };

    const carOwners = participants.filter(p => p.hasCar);
    if (carOwners.length === 0) return {
        departure: { groups: [], unassigned: participants },
        returnTrip: { groups: [], unassigned: participants }
    };

    const { depScores, retScores } = buildScores(participants, depRoutes, retRoutes);

    const carIdxs = participants.map((p, i) => p.hasCar ? i : -1).filter(i => i >= 0);
    const nonCarIdxs = participants.map((p, i) => !p.hasCar ? i : -1).filter(i => i >= 0);

    // 최적 드라이버 조합 선택
    const combos = getCombinations(carIdxs.length);
    let bestScore = Infinity;
    let bestCombo = null;

    for (const combo of combos) {
        const driverIdxs = combo.map(c => carIdxs[c]);
        const riderCarIdxs = carIdxs.filter(i => !driverIdxs.includes(i));
        const riderIdxs = [...nonCarIdxs, ...riderCarIdxs];

        const totalSeats = driverIdxs.reduce((sum, di) => sum + participants[di].seats, 0);
        if (totalSeats < riderIdxs.length) continue;

        const matrix = driverIdxs.map(di => riderIdxs.map(ri => {
            const dep = depScores[di][ri];
            const ret = retScores[di][ri];
            if (dep >= 99999 && ret >= 99999) return 99999;
            return (dep >= 99999 ? 0 : dep) + (ret >= 99999 ? 0 : ret);
        }));

        const seats = driverIdxs.map(di => participants[di].seats);
        const assigned = greedyAssign(driverIdxs.length, riderIdxs.length, seats, matrix);

        let totalScore = 0;
        let assignedCount = 0;
        for (let ri = 0; ri < riderIdxs.length; ri++) {
            if (assigned[ri] >= 0) {
                totalScore += matrix[assigned[ri]][ri];
                assignedCount++;
            }
        }

        totalScore += driverIdxs.length * 50;
        totalScore -= assignedCount * 30;

        if (totalScore < bestScore) {
            bestScore = totalScore;
            bestCombo = { driverIdxs, riderIdxs };
        }
    }

    if (!bestCombo) {
        bestCombo = { driverIdxs: carIdxs, riderIdxs: nonCarIdxs };
    }

    const drivers = bestCombo.driverIdxs.map(i => participants[i]);
    const riders = bestCombo.riderIdxs.map(i => participants[i]);

    // 출발 배정
    const depMatrix = bestCombo.driverIdxs.map(di =>
        bestCombo.riderIdxs.map(ri => depScores[di][ri])
    );
    const depSeats = drivers.map(d => d.seats);
    const depAssigned = greedyAssign(drivers.length, riders.length, depSeats, depMatrix);
    const departure = buildGroups(drivers, riders, depAssigned);

    // 귀환 배정 (출발에서 확정된 드라이버)
    const finalDrivers = departure.groups.map(g => g.driver);
    const finalRiders = participants.filter(p => !finalDrivers.includes(p));
    const finalDriverIdxs = finalDrivers.map(d => participants.indexOf(d));
    const finalRiderIdxs = finalRiders.map(r => participants.indexOf(r));

    const retMatrix = finalDriverIdxs.map(di =>
        finalRiderIdxs.map(ri => retScores[di][ri])
    );
    const retSeats = finalDrivers.map(d => d.seats);
    const retAssigned = greedyAssign(finalDrivers.length, finalRiders.length, retSeats, retMatrix);
    const returnTrip = buildGroups(finalDrivers, finalRiders, retAssigned);

    return { departure, returnTrip };
};
